/**
 * InvestigateFormsModal.tsx
 *
 * Helps recover a form that was filled out but shows as still "required" / missing
 * on the patient. Surfaces two groups:
 *   1. Unlinked (orphaned, patientID=0) forms matched to this patient — by a form
 *      link in the comm log, or by the patient's own email/phone entered on the form.
 *   2. This patient's own completed forms that were soft-deleted (can be reverted).
 *
 * Clicking a row opens the form's full content for verification; the Attach/Restore
 * action lives at the bottom of that preview.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, FileText, Link2, ChevronRight, Trash2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui';
import {
  getOrphanFormCandidates,
  type OrphanFormCandidate,
  type AttachOrphanResult,
} from '@/services/patientForms.service';
import { OrphanFormPreviewModal } from './OrphanFormPreviewModal';

interface InvestigateFormsModalProps {
  patientId: number;
  open: boolean;
  onClose: () => void;
}

/** Confidence badge: deterministic comm-log link vs. identity (email/phone/name/dob) match. */
function MatchBadge({ candidate }: { candidate: OrphanFormCandidate }) {
  if (candidate.category === 'deleted') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-rose-100 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700">
        <Trash2 className="h-3 w-3" /> Deleted — can restore
      </span>
    );
  }
  if (candidate.matchType === 'commlog-id') {
    return (
      <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
        Linked in comm log
      </span>
    );
  }

  const m = candidate.matches;
  const hits: string[] = [];
  if (m?.email) hits.push('email');
  if (m?.phone) hits.push('phone');
  if (m?.firstName && m?.lastName) hits.push('name');
  else if (m?.firstName) hits.push('first name');
  else if (m?.lastName) hits.push('last name');
  if (m?.dob) hits.push('DOB');

  // Email or phone is a strong unique identifier; name/DOB alone is weaker.
  const strong = !!(m?.email || m?.phone);
  const label = hits.length ? `Matches ${hits.join(' + ')}` : 'Possible match';

  return (
    <span
      className={
        strong
          ? 'inline-flex items-center rounded bg-sky-100 px-1.5 py-0.5 text-[11px] font-semibold text-sky-700'
          : 'inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-700'
      }
    >
      {label}
    </span>
  );
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function CandidateRow({ candidate, onClick }: { candidate: OrphanFormCandidate; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-start justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-slate-50"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <FileText className="h-4 w-4 flex-shrink-0 text-slate-400" />
            <span className="font-medium text-slate-800">{candidate.surveyTitle}</span>
            <span className="text-xs text-slate-400">{formatDateTime(candidate.dateCompleted)}</span>
            <MatchBadge candidate={candidate} />
          </div>
          {candidate.answerPreview && (
            <p className="mt-1 line-clamp-2 text-xs text-slate-500">{candidate.answerPreview}</p>
          )}
          {candidate.evidence && (
            <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
              <Link2 className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">
                From comm log ({formatDateTime(candidate.evidence.date)}): {candidate.evidence.preview}
              </span>
            </p>
          )}
          {candidate.category === 'deleted' && candidate.deletedDate && (
            <p className="mt-1 text-xs text-rose-500">Deleted {formatDateTime(candidate.deletedDate)}</p>
          )}
        </div>
        <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-indigo-600">
          Review <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </button>
    </li>
  );
}

export function InvestigateFormsModal({ patientId, open, onClose }: InvestigateFormsModalProps) {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);
  const [previewCandidate, setPreviewCandidate] = useState<OrphanFormCandidate | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-orphan-forms', patientId],
    queryFn: () => getOrphanFormCandidates(patientId),
    enabled: open,
  });

  const candidates: OrphanFormCandidate[] = data ?? [];
  const orphans = candidates.filter((c) => c.category !== 'deleted');
  const deleted = candidates.filter((c) => c.category === 'deleted');

  useEffect(() => {
    if (!open) {
      setFeedback(null);
      setPreviewCandidate(null);
    }
  }, [open]);

  const handleAttached = (result: AttachOrphanResult, candidate: OrphanFormCandidate) => {
    // Refresh the Forms tab list + General Intake banner, and this list.
    void queryClient.invalidateQueries({ queryKey: ['patient-forms-list', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient-orphan-forms', patientId] });
    const verb = candidate.category === 'deleted' ? 'restored to' : 'attached to';
    let msg = `Form ${verb} this patient.`;
    if (result.giBackfilled) msg += ' The General Intake requirement has been cleared.';
    if (result.recoveredAnswers > 0) {
      msg += ` Recovered ${result.recoveredAnswers} missing answer${result.recoveredAnswers === 1 ? '' : 's'} (e.g. signature / acceptance) from the original submission.`;
    }
    setFeedback(msg);
    setPreviewCandidate(null);
  };

  const isSuccessFeedback = !!feedback && /(attached|restored)/i.test(feedback);

  return (
    <>
      <Modal isOpen={open} onClose={onClose} title="Investigate missing forms" size="xl">
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Here are some possible matches. Click a form to review its full content, and please
            confirm before attaching.
          </p>

          {feedback && (
            <div
              className={
                isSuccessFeedback
                  ? 'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700'
                  : 'rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'
              }
            >
              {feedback}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : isError ? (
            <p className="py-4 text-sm text-red-600">Failed to load matches.</p>
          ) : candidates.length === 0 ? (
            <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
              <span>
                No unlinked or deleted forms could be matched to this patient. If you know a form was
                filled out, it may not carry the patient&apos;s email/phone or a form link, so it
                can&apos;t be matched automatically.
              </span>
            </div>
          ) : (
            <div className="space-y-5">
              {orphans.length > 0 && (
                <div>
                  <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Unlinked forms matched to this patient
                  </h3>
                  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                    {orphans.map((c) => (
                      <CandidateRow key={c.surveysResponseId} candidate={c} onClick={() => setPreviewCandidate(c)} />
                    ))}
                  </ul>
                </div>
              )}

              {deleted.length > 0 && (
                <div>
                  <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Deleted forms (can be restored)
                  </h3>
                  <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                    {deleted.map((c) => (
                      <CandidateRow key={c.surveysResponseId} candidate={c} onClick={() => setPreviewCandidate(c)} />
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      <OrphanFormPreviewModal
        patientId={patientId}
        candidate={previewCandidate}
        open={previewCandidate !== null}
        onClose={() => setPreviewCandidate(null)}
        onAttached={handleAttached}
      />
    </>
  );
}
